import { IAnswerMap, IAnswerValue, IQuestion } from "../types";

/**
 * Does an answer intersect a set of values?
 *
 * A multi_select answer is an array, so this is a non-empty intersection. Any
 * other answer is a scalar, so it is membership. One rule covers both, which is
 * why the config needs only `op: "in"`.
 */
export const answerMatches = (
  answer: IAnswerValue | undefined,
  values: string[]
): boolean => {
  if (answer == null) return false;

  if (Array.isArray(answer)) {
    // `picture_upload`'s answer is also an array (attachment ids), but no
    // config conditions on it — the `typeof` guard is just what keeps that
    // widened union honest for TypeScript.
    return answer.some(
      (value) => typeof value === "string" && values.includes(value)
    );
  }

  return values.includes(String(answer));
};

/**
 * A question with no `visible_if` is always shown. With conditions, every one
 * must hold.
 *
 * `answers` must be the answers merged across all screens, not just the current
 * one — painter's `rooms` depends on `surface_type`, which lives a screen back.
 *
 * Note: no config uses more than one condition today, so AND vs OR is not yet
 * observable. AND is the safer reading of a list of requirements.
 */
export const isVisible = (question: IQuestion, answers: IAnswerMap): boolean =>
  (question.visible_if ?? []).every((condition) =>
    answerMatches(answers[condition.question], condition.values)
  );

/**
 * Does this question render inside one of another question's option rows,
 * rather than as its own block?
 *
 * True when it depends on exactly one condition, naming exactly one value, on a
 * question that sits in the same screen. Across the real configs that matches
 * only `desired_date` inside the "before this date" radio — painter's `rooms`
 * and `floor_area_m2` depend on a question from a *different* screen, so they
 * stay ordinary blocks.
 *
 * Deriving it from the config rather than hardcoding screen keys means a future
 * "other → describe it" text field gets the same treatment for free.
 */
export const getInlineTrigger = (
  question: IQuestion,
  screenQuestions: IQuestion[]
): { questionKey: string; optionValue: string } | null => {
  const conditions = question.visible_if ?? [];
  if (conditions.length !== 1) return null;

  const [condition] = conditions;
  if (condition.values.length !== 1) return null;

  const isSibling = screenQuestions.some((q) => q.key === condition.question);
  if (!isSibling) return null;

  return { questionKey: condition.question, optionValue: condition.values[0] };
};
